import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlusCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { pollSchema } from '@/lib/validationSchemas';

interface PollCreatorProps {
  streamId?: string;
  onPollCreated?: (pollId: string) => void;
}

export const PollCreator = ({ streamId, onPollCreated }: PollCreatorProps) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [costPerVote, setCostPerVote] = useState(0);
  const [duration, setDuration] = useState(5); // minutes

  const addOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    } else {
      toast.error('Maximum 10 options allowed');
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    } else {
      toast.error('Minimum 2 options required');
    }
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const createPoll = async () => {
    const validOptions = options.filter(opt => opt.trim());
    
    // Validate poll data
    const validation = pollSchema.safeParse({
      question,
      options: validOptions,
      costPerVote,
      duration
    });

    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    // This would save to database when streamId is provided
    toast.success('Poll created! (Will save when stream starts)');
    
    // Reset form
    setQuestion('');
    setOptions(['', '']);
    setCostPerVote(0);
    setDuration(5);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Create Poll</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Question</Label>
          <Input
            placeholder="What should we do next?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={200}
          />
        </div>

        <div className="space-y-2">
          <Label>Options</Label>
          {options.map((option, index) => (
            <div key={index} className="flex gap-2">
              <Input
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={(e) => updateOption(index, e.target.value)}
                maxLength={100}
              />
              {options.length > 2 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOption(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {options.length < 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={addOption}
              className="w-full"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cost per Vote (coins)</Label>
            <Input
              type="number"
              min="0"
              max="1000"
              value={costPerVote}
              onChange={(e) => setCostPerVote(Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              min="1"
              max="60"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </div>
        </div>

        <Button onClick={createPoll} className="w-full">
          Create Poll
        </Button>
      </CardContent>
    </Card>
  );
};